/**
 * Loading matches, and putting the signals on them.
 *
 * **Cached-first, always.** The list renders from D1 the moment it arrives; the cron keeps it fresh
 * and a manual Refresh exists for impatience. The app never blocks on Polymarket — that was the
 * desktop's startup behaviour and it is the reason opening it felt slow.
 *
 * The propensity signals are computed HERE rather than in the Worker. A walk-forward pass is tens
 * of milliseconds of CPU per league against a ~10ms Workers budget, so the statistics live on the
 * client and in `trader/`. See DECISIONS.md.
 */
import { api } from '@/api';
import { LEAGUES, definitionFor, findLeague, parseTeamCodes } from '@/data/leagues';
import { toEspnCode } from '@/data/teamCodes';
import {
  BET_LINES,
  BET_NAMES,
  type BetKey,
  type BetLine,
  type Match,
  type PlacedBet,
  type Prediction,
} from '@/models';
import { aggregate, type LeagueStats } from '@/stats/leagueStats';
import { PropensityModel, type BetProbabilities } from '@/stats/propensity';
import type { MatchResult } from '@/stats/types';

interface RawMatch {
  event_id: string;
  slug: string | null;
  league: string | null;
  home_team: string | null;
  away_team: string | null;
  kickoff_utc: string | null;
  liquidity: number | null;
  volume: number | null;
  market_count: number | null;
  market_btts: number | null;
  market_btts_no: number | null;
  market_under35: number | null;
  market_over25: number | null;
  market_home_win: number | null;
  market_draw: number | null;
  market_away_win: number | null;
  us_btts: string | null;
  us_btts_no: string | null;
  us_under35: string | null;
  us_over25: string | null;
  us_home_win: string | null;
  us_draw: string | null;
  us_away_win: string | null;
  expected_total_goals: number | null;
  expected_home_goals: number | null;
  expected_away_goals: number | null;
  top_scores_json: string | null;
  explanation: string | null;
  btts_percent: number | null;
  under35_percent: number | null;
  over25_percent: number | null;
  home_win_percent: number | null;
  draw_percent: number | null;
  away_win_percent: number | null;
  btts_recommended: number | null;
  under35_recommended: number | null;
  over25_recommended: number | null;
  home_attack: string | null;
  away_attack: string | null;
  home_defense: string | null;
  away_defense: string | null;
  key_attacker_missing: number | null;
  key_defender_missing: number | null;
  open_game_expected: number | null;
  ran_at_utc: string | null;
  job_status: string | null;
  job_error: string | null;
}

const MARKET_COLUMN: Record<BetKey, keyof RawMatch> = {
  btts: 'market_btts',
  bttsNo: 'market_btts_no',
  under35: 'market_under35',
  over25: 'market_over25',
  homeWin: 'market_home_win',
  draw: 'market_draw',
  awayWin: 'market_away_win',
};

const US_COLUMN: Record<BetKey, keyof RawMatch> = {
  btts: 'us_btts',
  bttsNo: 'us_btts_no',
  under35: 'us_under35',
  over25: 'us_over25',
  homeWin: 'us_home_win',
  draw: 'us_draw',
  awayWin: 'us_away_win',
};

function line(r: RawMatch, key: BetKey): BetLine {
  return {
    key,
    name: BET_NAMES[key],
    aiPercent: null,
    marketPercent: (r[MARKET_COLUMN[key]] as number | null) ?? null,
    leaguePercent: null,
    profilePercent: null,
    profileFlagged: false,
    suggested: false,
    usMarketSlug: (r[US_COLUMN[key]] as string | null) ?? null,
  };
}

function toPrediction(r: RawMatch): Prediction | null {
  // `expected_total_goals` is NULL when the LEFT JOIN found no prediction. Do NOT guard on a
  // recommend_* flag: 0/1 is never NULL, so it always passes — a bug the desktop had once.
  if (r.expected_total_goals === null || r.expected_total_goals === undefined) return null;
  let topScores: string[] = [];
  try {
    const parsed = JSON.parse(r.top_scores_json ?? '[]');
    if (Array.isArray(parsed)) topScores = parsed.map(String);
  } catch {
    topScores = [];
  }
  return {
    expectedTotalGoals: r.expected_total_goals,
    expectedHomeGoals: r.expected_home_goals ?? 0,
    expectedAwayGoals: r.expected_away_goals ?? 0,
    topScores,
    explanation: r.explanation ?? '',
    homeAttack: r.home_attack,
    awayAttack: r.away_attack,
    homeDefense: r.home_defense,
    awayDefense: r.away_defense,
    keyAttackerMissing: !!r.key_attacker_missing,
    keyDefenderMissing: !!r.key_defender_missing,
    openGameExpected: !!r.open_game_expected,
    ranAtUtc: r.ran_at_utc,
  };
}

function toMatch(r: RawMatch): Match {
  const bets = {} as Record<BetKey, BetLine>;
  for (const k of BET_LINES) bets[k] = line(r, k);

  const p = toPrediction(r);
  if (p) {
    // The AI percentages fan out across the seven lines from the three the schema carries plus the
    // 1X2 trio. BTTS No is the complement of BTTS — one opinion, two sides.
    const set = (k: BetKey, v: number | null) => {
      if (v !== null && v !== undefined) bets[k].aiPercent = v;
    };
    set('btts', r.btts_percent);
    set('bttsNo', r.btts_percent === null ? null : 100 - r.btts_percent);
    set('under35', r.under35_percent);
    set('over25', r.over25_percent);
    // Imported desktop rows carry 0 for these — the old schema never stored them. Treated as
    // "not supplied" rather than as a genuine 0%.
    set('homeWin', r.home_win_percent || null);
    set('draw', r.draw_percent || null);
    set('awayWin', r.away_win_percent || null);
    bets.btts.suggested = !!r.btts_recommended;
    bets.under35.suggested = !!r.under35_recommended;
    bets.over25.suggested = !!r.over25_recommended;
  }

  return {
    eventId: r.event_id,
    slug: r.slug ?? '',
    league: r.league ?? '',
    homeTeam: r.home_team ?? '',
    awayTeam: r.away_team ?? '',
    kickoffUtc: r.kickoff_utc ?? '',
    liquidity: r.liquidity ?? 0,
    volume: r.volume ?? 0,
    marketCount: r.market_count ?? 0,
    bets,
    prediction: p,
    jobStatus: (r.job_status as Match['jobStatus']) ?? null,
    jobError: r.job_error,
    modelInfo: null,
  };
}

export async function fetchMatches(): Promise<Match[]> {
  const body = await api.get<{ matches: RawMatch[] }>('/api/matches');
  return (body.matches ?? []).map(toMatch);
}

export async function fetchBets(): Promise<PlacedBet[]> {
  const body = await api.get<{ bets: Record<string, unknown>[] }>('/api/bets');
  return (body.bets ?? []).map((b) => ({
    id: Number(b.id),
    eventId: String(b.event_id ?? ''),
    league: String(b.league ?? ''),
    homeTeam: String(b.home_team ?? ''),
    awayTeam: String(b.away_team ?? ''),
    kickoffUtc: String(b.kickoff_utc ?? ''),
    betName: String(b.bet_name ?? ''),
    requestedStake: Number(b.requested_stake ?? 0),
    shares: Number(b.shares ?? 0),
    cost: Number(b.cost ?? 0),
    avgPrice: Number(b.avg_price ?? 0),
    bestPrice: Number(b.best_price ?? 0),
    limitPrice: Number(b.limit_price ?? 0),
    isPartial: !!b.is_partial,
    aiPercent: b.ai_percent === null || b.ai_percent === undefined ? null : Number(b.ai_percent),
    marketPercent:
      b.market_percent === null || b.market_percent === undefined ? null : Number(b.market_percent),
    modelLeague: (b.model_league as string | null) ?? null,
    modelResults: Number(b.model_results ?? 0),
    signalEdge: b.signal_edge === null || b.signal_edge === undefined ? null : Number(b.signal_edge),
    signalZ: b.signal_z === null || b.signal_z === undefined ? null : Number(b.signal_z),
    marketCount: Number(b.market_count ?? 0),
    status: String(b.status ?? ''),
    outcome: (b.outcome as PlacedBet['outcome']) ?? 'Pending',
    placedAtUtc: String(b.placed_at_utc ?? ''),
  }));
}

export async function fetchResults(leagueCode: string): Promise<MatchResult[]> {
  const body = await api.get<{ results: MatchResult[] }>(
    `/api/results?league=${encodeURIComponent(leagueCode)}`,
  );
  return body.results ?? [];
}

export interface Scorecard {
  league_code: string;
  signal: string;
  bets: number;
  hit_rate: number;
  edge_points: number;
  z_score: number;
}

export async function fetchScorecards(): Promise<Scorecard[]> {
  const body = await api.get<{ scorecards: Scorecard[] }>('/api/scorecards');
  return body.scorecards ?? [];
}

export interface LeagueRow {
  code: string;
  displayName: string;
  espnSlug: string;
  stats: LeagueStats;
}

export async function fetchLeagues(): Promise<LeagueRow[]> {
  const body = await api.get<{ leagues: LeagueRow[] }>('/api/leagues');
  return body.leagues ?? [];
}

export const requestPrediction = (eventId: string) =>
  api.post(`/api/predictions/${encodeURIComponent(eventId)}`);

/** Nudges the driver rather than waiting for the next minute-tick. */
export const drivePredictions = () => api.post('/api/predictions/drive');

export const refreshMatches = () => api.post('/api/matches/refresh');

/**
 * A signal is DISABLED when this league's own backtest contradicts it on a meaningful sample.
 * Permissive on purpose: strict positive-evidence gating would blank every league whose sample is
 * still thin, and a thin sample is not evidence against.
 */
export const scorecardDisabled = (c: Scorecard | undefined) =>
  !!c && c.bets >= 50 && c.edge_points <= 0;

/** The strategy keys whose backtest rows gate the matching live flags. */
export const GATE_SIGNALS: Record<BetKey, string> = {
  btts: 'btts_flagged',
  over25: 'over_abs',
  under35: 'under_abs',
  bttsNo: 'bttsno_model',
  homeWin: 'home_model',
  draw: 'draw_model',
  awayWin: 'away_model',
};

/**
 * Attaches every league-derived signal to the matches: base rates, the propensity estimate, and
 * the flags — each gated by that league's own backtest scorecard.
 *
 * Mutates in place and returns the same array, because the store holds one list and re-deriving a
 * parallel copy is how two views come to disagree about what is flagged.
 */
export function applySignals(
  matches: Match[],
  resultsByLeague: Map<string, MatchResult[]>,
  scorecards: Scorecard[],
): Match[] {
  const cards = new Map<string, Scorecard>();
  for (const c of scorecards) cards.set(`${c.league_code}|${c.signal}`, c);

  const baselines = new Map<string, LeagueStats>();
  const models = new Map<string, PropensityModel>();
  for (const league of LEAGUES) {
    const results = resultsByLeague.get(league.code);
    if (!results?.length) continue;
    baselines.set(league.code, aggregate(league.code, league.displayName, results));
    models.set(league.code, PropensityModel.build(results));
  }

  for (const m of matches) {
    const def = definitionFor(m.slug, m.league);
    if (!def) continue;
    const stats = baselines.get(def.code);
    if (stats && stats.matches > 0) {
      m.bets.btts.leaguePercent = stats.bttsRate;
      m.bets.bttsNo.leaguePercent = 100 - stats.bttsRate;
      m.bets.over25.leaguePercent = stats.over25Rate;
      m.bets.under35.leaguePercent = stats.under35Rate;
      m.bets.homeWin.leaguePercent = stats.homeWinRate;
      m.bets.draw.leaguePercent = stats.drawRate;
      m.bets.awayWin.leaguePercent = stats.awayWinRate;
    }

    const model = models.get(def.code);
    if (!model) continue;

    // Team codes come from the Polymarket slug (`mls-nyr-clt-2026-07-25` -> nyr, clt), while stored
    // results carry ESPN abbreviations, so both sides go through the code map. A fixture with
    // either side unresolved simply gets no propensity signal — never a guessed one.
    const { home, away } = parseTeamCodes(m.slug);
    if (!home || !away) continue;
    const h = toEspnCode(def.code, home);
    const a = toEspnCode(def.code, away);
    const probs = model.probabilities(h, a);
    if (!probs) continue;

    applyProfile(m, probs, model.isLikelyBtts(h, a), model, def.code, cards);
    m.modelInfo = `${def.displayName} · ${model.matchesUsed} results`;
  }

  return matches;
}

function applyProfile(
  m: Match,
  p: BetProbabilities,
  bttsFlagged: boolean,
  model: PropensityModel,
  leagueCode: string,
  cards: Map<string, Scorecard>,
): void {
  const allowed = (key: BetKey) => !scorecardDisabled(cards.get(`${leagueCode}|${GATE_SIGNALS[key]}`));

  m.bets.btts.profilePercent = p.btts * 100;
  m.bets.btts.profileFlagged = bttsFlagged && allowed('btts');

  m.bets.over25.profilePercent = p.over25 * 100;
  m.bets.over25.profileFlagged = model.flagsOver25(p.over25) && allowed('over25');

  m.bets.under35.profilePercent = p.under35 * 100;
  m.bets.under35.profileFlagged = model.flagsUnder35(p.under35) && allowed('under35');

  // BTTS No mirrors the BTTS flag; flagging both would be one opinion counted twice.
  m.bets.bttsNo.profilePercent = p.bttsNo * 100;
  m.bets.bttsNo.profileFlagged = !bttsFlagged && model.flagsBttsNo(p.bttsNo) && allowed('bttsNo');

  m.bets.homeWin.profilePercent = p.homeWin * 100;
  m.bets.draw.profilePercent = p.draw * 100;
  m.bets.awayWin.profilePercent = p.awayWin * 100;

  // Only the strongest result line may flag — home, draw and away sum to 1, so flagging more than
  // one is a guaranteed loss after fees.
  const best = Math.max(p.homeWin, p.draw, p.awayWin);
  m.bets.homeWin.profileFlagged = p.homeWin === best && model.flagsHomeWin(p.homeWin) && allowed('homeWin');
  m.bets.draw.profileFlagged =
    p.draw === best && p.draw !== p.homeWin && model.flagsDraw(p.draw) && allowed('draw');
  m.bets.awayWin.profileFlagged =
    p.awayWin === best &&
    p.awayWin !== p.homeWin &&
    p.awayWin !== p.draw &&
    model.flagsAwayWin(p.awayWin) &&
    allowed('awayWin');
}

/** Which leagues the current match list actually needs results for. Keeps the phone's work down. */
export function leaguesInPlay(matches: Match[]): string[] {
  const codes = new Set<string>();
  for (const m of matches) {
    const def = definitionFor(m.slug, m.league);
    if (def) codes.add(def.code);
  }
  return [...codes].filter((c) => findLeague(c));
}
